<template>
  <!-- Design: CoursePanel. Opened by any 'bv::toggle::collapse' with 'course-sidebar' (result list,
       notebook, courses modal) through openCourse(). The course text stays the job server's page
       (showkurs): the API has no course details. -->
  <div v-if="open && course" class="ts-course-panel-root">
    <div class="offcanvas-backdrop fade show ts-jobad-backdrop" aria-hidden="true" @click="close"></div>
    <aside
      ref="panel"
      class="offcanvas offcanvas-end ts-jobad ts-course-panel show"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tsCoursePanelTitle"
      tabindex="-1"
      @keydown.esc.stop="close"
    >
      <ts-panel-header
        class="ts-course-panel-topbar"
        :previous-label="text.previousCourse"
        :next-label="text.nextCourse"
        :previous-disabled="index <= 0"
        :next-disabled="index >= list.length - 1"
        @close="close"
        @previous="goTo(index - 1)"
        @next="goTo(index + 1)"
      />

      <!-- Design: the panel title sits in a tab row under the bar, like the job panel -->
      <div class="ts-job-ad-tabrow d-flex align-items-center border-bottom">
        <nav class="ts-job-ad-tabs flex-grow-1" :aria-label="text.courseOffer">
          <div class="nav nav-tabs border-0">
            <h2 id="tsCoursePanelTitle" class="nav-link active mb-0">
              <i class="bi bi-mortarboard" aria-hidden="true"></i>{{ text.courseOffer }}
            </h2>
          </div>
        </nav>
      </div>

      <div class="offcanvas-body p-0">
        <div class="ts-course-layout d-flex h-100">
          <aside class="ts-course-summary">
            <div class="ts-panel-summary-inner p-4">
              <div class="ts-course-provider-tile d-flex align-items-center justify-content-center fw-bold mx-auto">
                {{ initials }}
              </div>
              <a class="ts-panel-info-row ts-panel-info-action mt-4" :href="course.url" target="_blank" rel="noopener">
                <i class="bi bi-box-arrow-up-right ts-panel-info-icon" aria-hidden="true"></i>
                <span class="ts-panel-info-text">{{ text.originalAd }}</span>
              </a>
              <button
                class="btn btn-dark w-100 mt-2"
                type="button"
                :title="isSaved ? translations[14012] : text.rememberCourse"
                :aria-pressed="String(isSaved)"
                @click="toggleNotepad"
              >
                <i class="bi me-2" :class="isSaved ? 'bi-bookmark-check-fill' : 'bi-bookmark'" aria-hidden="true"></i>
                {{ isSaved ? translations[10983] : text.rememberCourse }}
              </button>

              <hr class="my-4" />
              <div class="ts-panel-info-list">
                <div v-if="course.provider" class="ts-panel-info-row" :class="{ 'user-select-none blur-5': isProviderHidden }">
                  <i class="bi bi-building ts-panel-info-icon" aria-hidden="true"></i>
                  <strong class="ts-panel-info-text">{{ course.provider }}</strong>
                </div>
                <div v-if="course.startDate" class="ts-panel-info-row">
                  <i class="bi bi-calendar-event ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ course.startDate }}</span>
                </div>
                <div v-if="course.location" class="ts-panel-info-row">
                  <i class="bi bi-geo-alt ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ course.location }}</span>
                </div>
              </div>

              <hr class="my-4" />
              <div class="ts-job-eyebrow mb-2">{{ text.moreActions }}</div>
              <div class="ts-panel-info-list">
                <!-- The short link /courses/<id> to share (sections/shareLinks.ts) -->
                <button class="btn ts-panel-info-row ts-panel-info-action" type="button" @click="copyLink">
                  <i class="bi ts-panel-info-icon" :class="linkCopied ? 'bi-check-lg' : 'bi-link-45deg'" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ copyLinkText }}</span>
                </button>
                <template v-if="hasPlace">
                  <a class="ts-panel-info-row ts-panel-info-action" :href="mapUrl" target="_blank" rel="noopener">
                    <i class="bi bi-map ts-panel-info-icon" aria-hidden="true"></i>
                    <span class="ts-panel-info-text">{{ text.mapLabel }}</span>
                  </a>
                  <a class="ts-panel-info-row ts-panel-info-action" :href="routeUrl" target="_blank" rel="noopener">
                    <i class="bi bi-geo-alt ts-panel-info-icon" aria-hidden="true"></i>
                    <span class="ts-panel-info-text">{{ text.route }}</span>
                  </a>
                </template>
                <button class="btn ts-panel-info-row ts-panel-info-action" type="button" @click="print">
                  <i class="bi bi-printer ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ translations[1983] }}</span>
                </button>
                <!-- From the course page's own bar (hidden): Google translation, Wikipedia, public transport -->
                <ts-panel-language
                  v-if="barLanguages.length"
                  :languages="barLanguages"
                  :prompt="barLanguagePrompt"
                  :value="barLanguage"
                  @select="translateCourse"
                />
                <button
                  v-for="action in barActions"
                  :key="action.key"
                  class="btn ts-panel-info-row ts-panel-info-action"
                  type="button"
                  :title="action.title || null"
                  @click="runBarAction(action)"
                >
                  <i class="bi ts-panel-info-icon" :class="action.icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ action.label }}</span>
                </button>
              </div>
              <div class="ts-panel-info-id mt-4 pt-3">
                <i class="bi bi-hash ts-panel-info-icon" aria-hidden="true"></i>
                <small class="ts-panel-info-text">{{ text.courseIdLabel }}: {{ course.id }}</small>
                <button class="btn ts-copy-button" type="button" :aria-label="text.copyCourseId" :title="text.copyCourseId" @click="copyCourseId">
                  <i class="bi" :class="copied ? 'bi-check-lg' : 'bi-copy'" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </aside>
          <article class="ts-course-content position-relative p-0">
            <div v-if="iframeLoading" class="ts-loading d-flex align-items-center justify-content-center" role="status">
              <span class="spinner-border" aria-hidden="true"></span>
              <span class="visually-hidden">{{ text.loading }}</span>
            </div>
            <iframe
              ref="courseFrame"
              :key="course.url"
              class="ts-job-ad-frame"
              :title="course.title || text.courseDetailLabel"
              :src="frameUrl"
              allowfullscreen
              @load="onFrameLoad"
            ></iframe>
          </article>
        </div>
      </div>
    </aside>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { copyLinkLabel, copyText, shareLink } from '@/sections/shareLinks';
import { UserModule } from '@/store/modules/user';
import { CourseModalModule } from '@/store/modules/courses/courseModal';
import { groupCurses } from '@/config/configuration';
import {
  CourseRow, isCourseInNotebook, toCourseRow, toggleCourseNotepad,
} from '@/sections/courses/search';
import TsPanelHeader from '@/components/Talentspace/detail/TsPanelHeader.vue';
import TsPanelLanguage from '@/components/Talentspace/detail/TsPanelLanguage.vue';
import {
  addFrameStyle, FrameAction, frameActionsSelector, FrameLanguage, frameDocumentOf,
  readFrameActions, readFrameLanguages, runFrameAction, selectFrameLanguage,
} from '@/sections/frameBar';

// The course page's bar links the left panel shows; map and route are there already
const BAR_ACTIONS_SELECTOR = frameActionsSelector(['icon-picture', 'icon-bus']);

const JOB_SERVER_JOBNET = 'https://job-server.net/jobnet/';

@Component({
  components: { TsPanelHeader, TsPanelLanguage },
})
export default class TsCoursePanel extends Vue {
  open = false;

  iframeLoading = true;

  // Taken over from the course page's own bar (the bar itself is hidden)
  barActions: FrameAction[] = [];

  barLanguages: FrameLanguage[] = [];

  barLanguagePrompt = '';

  barLanguage = '';

  copied = false;

  linkCopied = false;

  // Focus goes back here when the panel closes
  opener: HTMLElement | null = null;

  get translations() {
    return AppModule.translation;
  }

  get text() {
    return {
      previousCourse: AppModule.translation[16573],
      nextCourse: AppModule.translation[16574],
      courseDetailLabel: AppModule.translation[12210],
      // No string id for "Kursangebot" yet — German until one is created
      courseOffer: 'Kursangebot',
      rememberCourse: AppModule.translation[16575],
      courseIdLabel: AppModule.translation[16576],
      copyCourseId: AppModule.translation[16577],
      originalAd: AppModule.translation[16564],
      moreActions: AppModule.translation[5402],
      route: AppModule.translation[2811],
      mapLabel: AppModule.translation[2102],
      loading: AppModule.translation[16555],
    };
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  get isProviderHidden() {
    return Boolean(AppModule.settingPortal.not_logged_in_blur_employer_data) && !this.isAuth;
  }

  // ─── course ──────────────────────────────────────────────────────────────────

  // The job server's course page through this portal's own /jobnet (production), so its bar
  // can be read and hidden; other pages (and local development) stay as they are
  get frameUrl(): string {
    const url = this.course?.url || '';
    if (process.env.NODE_ENV !== 'production') return url;
    if (!url.startsWith(JOB_SERVER_JOBNET)) return url;
    return `/jobnet/${url.slice(JOB_SERVER_JOBNET.length)}`;
  }

  get list(): any[] {
    return CourseModalModule.courseListForModal || [];
  }

  get index(): number {
    return CourseModalModule.currentIndex;
  }

  get course(): CourseRow | null {
    const current = CourseModalModule.currentCourse as any;
    return current?.id ? toCourseRow(current) : null;
  }

  // Provider initials for the tile ("Lernwerk Nord Akademie" → "LN")
  get initials(): string {
    const words = (this.course?.provider || this.course?.title || '').split(/\s+/).filter((word) => /^[\p{L}\d]/u.test(word));
    return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('');
  }

  get isSaved(): boolean {
    return this.course ? isCourseInNotebook(this.course.id) : false;
  }

  // "Bundesweit" courses have no place to show
  get hasPlace(): boolean {
    return Boolean(this.course?.location) && this.course?.location !== 'Bundesweit';
  }

  get mapUrl(): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.course?.location || '')}`;
  }

  get routeUrl(): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(this.course?.location || '')}`;
  }

  // ─── open / close ────────────────────────────────────────────────────────────

  onToggle(id: string) {
    if (id !== 'course-sidebar') return;
    if (this.open) this.close();
    else this.show();
  }

  show() {
    this.opener = document.activeElement as HTMLElement | null;
    this.open = true;
    this.iframeLoading = true;
    document.body.classList.add('ts-jobad-open');
    this.$nextTick(() => (this.$refs.panel as HTMLElement | undefined)?.focus());
  }

  close() {
    this.open = false;
    document.body.classList.remove('ts-jobad-open');
    this.opener?.focus?.();
  }

  @Watch('course.url')
  onCourseChange() {
    this.iframeLoading = true;
    this.barActions = [];
    this.barLanguages = [];
  }

  async goTo(index: number) {
    const course = this.list[index];
    if (!course) return;
    CourseModalModule.UPDATE_CURRENT_COURSE(course);
    await CourseModalModule.UPDATE_CURRENT_COURSE_INDEX(index);
  }

  // ─── actions ─────────────────────────────────────────────────────────────────

  // Kept in the browser, as the result card
  toggleNotepad() {
    if (!this.course) return;
    if (!this.isAuth && !groupCurses(AppModule.sid)) this.$bvModal.show('not-auth-modal');
    toggleCourseNotepad(this.course);
  }

  // The course page is on the job server: it prints itself when it allows it, else opens to print
  frameDocument(): Document | null {
    return frameDocumentOf(this.$refs.courseFrame as HTMLIFrameElement | undefined);
  }

  onFrameLoad() {
    this.iframeLoading = false;
    const page = this.frameDocument();
    this.barActions = readFrameActions(page, BAR_ACTIONS_SELECTOR);
    const { prompt, languages } = readFrameLanguages(page?.querySelector('.button-container select.button-select') || null);
    this.barLanguagePrompt = prompt;
    this.barLanguages = languages;
    this.barLanguage = '';
    if (page?.querySelector('.frame_bottom .button-container')) {
      addFrameStyle(page, 'ts-hide-bar', '.frame_bottom, .hamburger { display: none !important; } .frame_main { height: 100% !important; bottom: 0 !important; }');
    }
  }

  // The bar's own select translates the course (translate_page)
  translateCourse(value: string) {
    selectFrameLanguage(this.frameDocument()?.querySelector('.button-container select.button-select') || null, value);
    this.barLanguage = value;
  }

  runBarAction(action: FrameAction) {
    runFrameAction(this.frameDocument(), BAR_ACTIONS_SELECTOR, action);
  }

  print() {
    const frame = this.$refs.courseFrame as HTMLIFrameElement | undefined;
    try {
      frame?.contentWindow?.focus();
      frame?.contentWindow?.print();
    } catch {
      window.open(this.course?.url, '_blank', 'noopener');
    }
  }

  get copyLinkText() {
    return copyLinkLabel();
  }

  async copyLink() {
    await copyText(shareLink('courses', this.course?.id));
    this.linkCopied = true;
    window.setTimeout(() => { this.linkCopied = false; }, 1500);
  }

  async copyCourseId() {
    const value = String(this.course?.id ?? '');
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }
    this.copied = true;
    window.setTimeout(() => { this.copied = false; }, 1500);
  }

  created() {
    this.$root.$on('bv::toggle::collapse', this.onToggle);
  }

  beforeDestroy() {
    this.$root.$off('bv::toggle::collapse', this.onToggle);
    document.body.classList.remove('ts-jobad-open');
  }
}
</script>
